import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Grid, FormControlLabel, Checkbox } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import useGetDailyFormInspectionQuestions from "hooks/fleet/useGetDailyFormInspectionQuestions";
import useGetDailyFormVehicles from "hooks/fleet/useGetDailyFormVehicles";
import {
  CreateDailyFormInspectionInput,
  DailyFormInspectionQuestionInput,
  DailyFormInspectionQuestionType,
  UpdateDailyFormInspectionInput,
  UserStatus,
} from "generated/graphql";

interface DailyInspectionFormData {
  dailyFormVehicleId?: string;
  vehicleMilleage: number;
  questionAnswers: DailyFormInspectionQuestionInput[];
}

type DailyInspectionFormType = "create" | "edit";

interface DailyInspectionFormProps {
  type?: DailyInspectionFormType;
  onSubmit: (data: CreateDailyFormInspectionInput | UpdateDailyFormInspectionInput) => void;
  onCancel: () => void;
  loading?: boolean;
  inspection?: any;
}

const DailyInspectionForm = React.memo(function DailyInspectionForm({
  type = "create",
  onSubmit,
  onCancel,
  loading = false,
  inspection,
}: DailyInspectionFormProps) {
  const [getDailyFormInspectionQuestions, { data: questions, loading: loadingQuestions }] =
    useGetDailyFormInspectionQuestions();

  const [getDailyFormVehicles, { data: vehiclesData, loading: loadingVehicles }] =
    useGetDailyFormVehicles();

  useEffect(() => {
    getDailyFormInspectionQuestions();
    getDailyFormVehicles({ first: 100, page: 1 });
  }, [getDailyFormInspectionQuestions, getDailyFormVehicles]);

  const vehicles = useMemo(() => {
    return vehiclesData || [];
  }, [vehiclesData]);

  const sortedQuestions = useMemo(() => {
    return questions?.slice().sort((a, b) => {
      if (
        a.type === DailyFormInspectionQuestionType.BOOLEAN &&
        b.type === DailyFormInspectionQuestionType.TEXT
      ) {
        return -1;
      }
      if (
        a.type === DailyFormInspectionQuestionType.TEXT &&
        b.type === DailyFormInspectionQuestionType.BOOLEAN
      ) {
        return 1;
      }
      return 0;
    });
  }, [questions]);

  const defaultValues = useMemo((): DailyInspectionFormData => {
    const questionAnswers: DailyFormInspectionQuestionInput[] = [];

    sortedQuestions?.forEach((question) => {
      const existingAnswer = inspection?.questionAnswers?.find(
        (qa: any) => qa.question?.id === question.id
      );

      if (existingAnswer) {
        questionAnswers.push({
          questionId: question.id,
          answer: existingAnswer.answer || undefined,
          isTrue: existingAnswer.isTrue || false,
        });
      } else {
        questionAnswers.push({
          questionId: question.id,
          answer: question.type === DailyFormInspectionQuestionType.TEXT ? "" : undefined,
          isTrue: question.type === DailyFormInspectionQuestionType.BOOLEAN ? false : undefined,
        });
      }
    });

    return {
      dailyFormVehicleId: inspection?.dailyFormVehicle?.id || "",
      vehicleMilleage: inspection?.vehicleMilleage || 0,
      questionAnswers,
    };
  }, [sortedQuestions, inspection]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DailyInspectionFormData>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const vehicleOptions = useMemo(
    () =>
      vehicles?.map((vehicle) => ({
        value: vehicle.id,
        label: vehicle.name,
      })) || [],
    [vehicles]
  );

  const handleFormSubmit = useCallback(
    (data: DailyInspectionFormData) => {
      const submitData: CreateDailyFormInspectionInput | UpdateDailyFormInspectionInput = {
        dailyFormVehicleId: data.dailyFormVehicleId,
        vehicleMilleage: data.vehicleMilleage,
        questionAnswers: {
          upsert: data.questionAnswers,
        },
      };

      if (type === "edit" && inspection?.id) {
        const updateData: UpdateDailyFormInspectionInput = {
          id: inspection.id,
          ...submitData,
        };
        onSubmit(updateData);
      } else {
        onSubmit(submitData);
      }
    },
    [type, inspection?.id, onSubmit]
  );

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  if (loadingQuestions || loadingVehicles) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <MDTypography variant="body2">Loading...</MDTypography>
      </MDBox>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              Select Vehicle *
            </MDTypography>
            <Controller
              name="dailyFormVehicleId"
              control={control}
              rules={{ required: "Vehicle selection is required" }}
              render={({ field, fieldState: { error } }) => (
                <AutoComplete
                  field={field}
                  options={vehicleOptions}
                  loading={loadingVehicles}
                  error={error}
                  label="Select Vehicle"
                  variant="outlined"
                />
              )}
            />
            {errors.dailyFormVehicleId && (
              <MDTypography variant="caption" color="error" mt={1}>
                {errors.dailyFormVehicleId.message}
              </MDTypography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <MDTypography variant="body2" fontWeight="medium" mb={1}>
              Vehicle mileage
            </MDTypography>
            <Controller
              name="vehicleMilleage"
              control={control}
              rules={{
                required: "Vehicle mileage is required",
                min: { value: 0, message: "Mileage must be positive" },
              }}
              render={({ field: { onChange, value, ...fieldProps } }) => (
                <MDInput
                  {...fieldProps}
                  value={value ? value.toLocaleString() : ""}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, "");
                    const numericValue = rawValue === "" ? "" : Number(rawValue);
                    onChange(numericValue);
                  }}
                  placeholder="Enter vehicle mileage"
                  fullWidth
                  error={!!errors.vehicleMilleage}
                />
              )}
            />
            {errors.vehicleMilleage && (
              <MDTypography variant="caption" color="error" mt={1}>
                {errors.vehicleMilleage.message}
              </MDTypography>
            )}
          </Grid>

          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Inspection Questions
              </MDTypography>
            </MDBox>
          </Grid>

          {sortedQuestions && sortedQuestions.length > 0 ? (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {sortedQuestions.map((question, index) => (
                  <Grid item xs={12} key={question.id}>
                    {question.type === DailyFormInspectionQuestionType.BOOLEAN ? (
                      <Controller
                        name={`questionAnswers.${index}.isTrue`}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value || false}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                            }
                            label={question.question}
                          />
                        )}
                      />
                    ) : (
                      <>
                        <MDTypography variant="body2" fontWeight="medium" mb={1}>
                          {question.question}
                        </MDTypography>
                        <Controller
                          name={`questionAnswers.${index}.answer`}
                          control={control}
                          render={({ field }) => (
                            <MDInput
                              {...field}
                              value={field.value || ""}
                              placeholder="Enter your answer"
                              fullWidth
                              multiline
                              rows={2}
                            />
                          )}
                        />
                      </>
                    )}
                    <Controller
                      name={`questionAnswers.${index}.questionId`}
                      control={control}
                      defaultValue={question.id}
                      render={() => null}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <MDTypography variant="body2" color="text" textAlign="center">
                No questions available. Please contact your administrator to add inspection
                questions.
              </MDTypography>
            </Grid>
          )}

          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                type="submit"
                disabled={loading || loadingVehicles || loadingQuestions}
              >
                {loading
                  ? type === "edit"
                    ? "Updating..."
                    : "Creating..."
                  : type === "edit"
                  ? "Update Inspection"
                  : "Create Inspection"}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </form>
    </>
  );
});

export default DailyInspectionForm;
